<script setup>
/**
 * ClientsDetailsModal
 * Modal showing detailed client breakdown for economic goal
 */
import { ref, computed } from 'vue'
import AppButton from '@/ui/components/AppButton.vue'

const props = defineProps({
  goal: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Filters
const search = ref('')
const selectedDay = ref('')
const selectedWeek = ref('')
const clientsIndication = ref('הכל')

// Sorting
const selectedSorted = ref({
  name: '',
  order: false,
})

// Day and week options
const dayOptions = ['א', 'ב', 'ג', 'ד', 'ה']
const weekOptions = ['1', '2', '3', '4']
const indicationOptions = ['הכל', 'לקוחות שלא סופקו', 'לקוחות שסופקו']

// Format date for display
const formattedDate = computed(() => {
  if (!props.goal.date_update) return 'לא צויין'
  const date = new Date(props.goal.date_update.seconds * 1000)
  return date.toLocaleString('he-IL')
})

// Filter clients based on search and filters
const filteredClients = computed(() => {
  let clients = []

  if (clientsIndication.value === 'הכל') {
    clients = [...(props.goal.clients || []), ...(props.goal.missing_clients || [])]
  } else if (clientsIndication.value === 'לקוחות שלא סופקו') {
    clients = props.goal.missing_clients || []
  } else if (clientsIndication.value === 'לקוחות שסופקו') {
    clients = props.goal.clients || []
  }

  return clients.filter((client) => {
    // Search filter
    const matchesSearch =
      !search.value ||
      client.makat?.includes(search.value) ||
      client.name?.includes(search.value) ||
      client.city?.includes(search.value)

    // Day filter
    const matchesDay = !selectedDay.value || client.visitation?.DAYNAME === selectedDay.value

    // Week filter
    const matchesWeek =
      !selectedWeek.value || client?.visitation?.[`WEEK${selectedWeek.value}`] === 'Y'

    return matchesSearch && matchesDay && matchesWeek
  })
})

// Sort clients
const sortedClients = computed(() => {
  if (!selectedSorted.value.name) return filteredClients.value

  const clients = [...filteredClients.value]
  const { name, order } = selectedSorted.value

  clients.sort((a, b) => {
    let valA, valB

    switch (name) {
      case 'makat':
        valA = Number(a.makat) || 0
        valB = Number(b.makat) || 0
        break
      case 'name':
        valA = a.name || ''
        valB = b.name || ''
        break
      case 'city':
        valA = a.city?.trim() || 'לא צויין'
        valB = b.city?.trim() || 'לא צויין'
        break
      case 'previous':
        valA = Number(a.previous_amount) || 0
        valB = Number(b.previous_amount) || 0
        break
      case 'current':
        valA = Number(a.amount) || 0
        valB = Number(b.amount) || 0
        break
      case 'diff':
        valA = (Number(a.amount) || 0) - (Number(a.previous_amount) || 0)
        valB = (Number(b.amount) || 0) - (Number(b.previous_amount) || 0)
        break
      default:
        return 0
    }

    if (typeof valA === 'string') {
      return order ? valA.localeCompare(valB) : valB.localeCompare(valA)
    }

    return order ? valA - valB : valB - valA
  })

  return clients
})

// Handle sort click
function handleSort(columnName) {
  if (selectedSorted.value.name === columnName) {
    selectedSorted.value.order = !selectedSorted.value.order
  } else {
    selectedSorted.value.name = columnName
    selectedSorted.value.order = false
  }
}

// Calculate difference
function getDifference(client) {
  const current = Number(client.amount) || 0
  const previous = Number(client.previous_amount) || 0
  return current - previous
}

// Handle backdrop click
function handleBackdropClick(e) {
  if (e.target === e.currentTarget) {
    emit('close')
  }
}
</script>

<template>
  <Teleport to="body">
    <div class="modal-overlay" @click="handleBackdropClick">
      <div class="modal-container">
        <!-- Header -->
        <div class="modal-header">
          <div class="header-info">
            <h3>{{ goal.name }}</h3>
            <small>עודכן: {{ formattedDate }}</small>
          </div>
          <button class="close-btn" @click="$emit('close')">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Filters -->
        <div class="modal-filters">
          <input v-model="search" type="text" class="filter-input" placeholder="חיפוש..." />

          <select v-model="selectedDay" class="filter-select">
            <option value="">בחר יום</option>
            <option v-for="day in dayOptions" :key="day" :value="day">יום {{ day }}'</option>
          </select>

          <select v-model="selectedWeek" class="filter-select">
            <option value="">בחר שבוע</option>
            <option v-for="week in weekOptions" :key="week" :value="week">שבוע {{ week }}</option>
          </select>

          <select v-model="clientsIndication" class="filter-select">
            <option v-for="option in indicationOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </div>

        <!-- Table -->
        <div class="modal-content">
          <table class="clients-table">
            <thead>
              <tr>
                <th @click="handleSort('makat')">
                  <span class="sort-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
                    </svg>
                  </span>
                  מס לקוח
                </th>
                <th @click="handleSort('name')">
                  <span class="sort-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
                    </svg>
                  </span>
                  לקוח
                </th>
                <th @click="handleSort('city')">
                  <span class="sort-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
                    </svg>
                  </span>
                  עיר
                </th>
                <th @click="handleSort('previous')">
                  <span class="sort-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
                    </svg>
                  </span>
                  חודש קודם
                </th>
                <th @click="handleSort('current')">
                  <span class="sort-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
                    </svg>
                  </span>
                  ביצוע נוכחי
                </th>
                <th @click="handleSort('diff')">
                  <span class="sort-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
                    </svg>
                  </span>
                  הפרש
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="sortedClients.length === 0">
                <td colspan="6" class="no-results">לא נמצאו תוצאות</td>
              </tr>
              <tr
                v-for="client in sortedClients"
                :key="client.makat"
                :class="{ 'zero-amount': (client.amount || 0) === 0 }"
              >
                <td>{{ client.makat }}</td>
                <td>{{ client.name || 'לא צויין שם' }}</td>
                <td>{{ client.city || 'לא צויין' }}</td>
                <td dir="ltr">&#8362; {{ (client.previous_amount || 0).toFixed(2) }}</td>
                <td dir="ltr">&#8362; {{ (client.amount || 0).toFixed(2) }}</td>
                <td dir="ltr" :class="getDifference(client) >= 0 ? 'positive' : 'negative'">
                  &#8362; {{ getDifference(client).toFixed(2) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
}

.modal-container {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  width: 100%;
  max-width: 1200px;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-xl);
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.header-info h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.header-info small {
  color: var(--text-muted);
  font-size: var(--font-size-xs);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Filters */
.modal-filters {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-bottom: 1px solid var(--border-color);
  flex-wrap: wrap;
}

.filter-input,
.filter-select {
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  min-width: 120px;
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

/* Table */
.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-sm);
}

.clients-table {
  width: 100%;
  border-collapse: collapse;
}

.clients-table th,
.clients-table td {
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: center;
  border: 1px solid var(--border-color);
}

.clients-table th {
  background: #04aa6d;
  color: white;
  font-weight: var(--font-weight-semibold);
  position: sticky;
  top: 0;
  cursor: pointer;
  user-select: none;
}

.clients-table th:hover {
  background: #03995f;
}

.sort-icon {
  display: inline-flex;
  margin-left: 4px;
  vertical-align: middle;
}

.clients-table tbody tr:nth-child(even) {
  background: var(--bg-secondary);
}

.clients-table tbody tr:nth-child(odd) {
  background: var(--bg-tertiary);
}

.clients-table tbody tr:hover {
  background: rgba(99, 102, 241, 0.1);
}

.clients-table tbody tr.zero-amount {
  color: var(--color-error);
}

.no-results {
  text-align: center;
  padding: var(--spacing-xl) !important;
  color: var(--text-muted);
}

.positive {
  color: var(--color-success);
  font-weight: var(--font-weight-medium);
}

.negative {
  color: var(--color-error);
  font-weight: var(--font-weight-medium);
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    height: 100vh;
    border-radius: 0;
  }

  .modal-filters {
    flex-direction: column;
  }

  .filter-input,
  .filter-select {
    width: 100%;
  }

  .clients-table {
    font-size: var(--font-size-xs);
  }

  .clients-table th,
  .clients-table td {
    padding: var(--spacing-xs);
  }
}
</style>
